import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Download } from "lucide-react";
import Logo from "./Logo";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { useLang } from "../context/LanguageContext";
import { useInstall } from "../context/InstallContext";
import LanguageSwitcher from "./LanguageSwitcher";

const ALL_LINKS = [
  { to: "/", tk: "nav_home", key: null },
  { to: "/comment-ca-marche", tk: "nav_how", key: "nav_how" },
  { to: "/challenge", tk: "nav_challenge", key: "nav_challenge" },
  { to: "/applications", tk: "nav_gallery", key: "nav_gallery" },
  { to: "/blog", tk: "nav_blog", key: "nav_blog" },
];

export default function PublicNav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();
  const { user } = useAuth();
  const { settings } = useSettings();
  const { t } = useLang();
  const { open: openInstall, canInstall } = useInstall();
  const LINKS = ALL_LINKS.filter((l) => !l.key || settings[l.key]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background,border] duration-300 ${
        scrolled ? "glass border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
      data-testid="public-nav"
    >
      <nav className="relative mx-auto flex max-w-[1400px] items-center justify-between px-5 py-4 sm:px-8">
        <Link to="/" data-testid="nav-home-logo"><Logo /></Link>

        <div className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
              className={`text-sm transition-colors hover:text-white ${
                loc.pathname === l.to ? "text-cyan" : "text-ink-secondary"
              }`}
            >
              {t(l.tk)}
            </Link>
          ))}
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          <LanguageSwitcher />
          {canInstall && (
            <button
              data-testid="nav-install-app"
              onClick={openInstall}
              title={t("pwa_menu")}
              className="group inline-flex items-center gap-1.5 border border-white/15 px-4 py-2 text-sm text-white transition-all hover:-translate-y-0.5 hover:border-cyan"
            >
              <Download size={15} className="text-cyan" /> {t("pwa_menu")}
            </button>
          )}
          {user ? (
            <button
              data-testid="nav-dashboard-btn"
              onClick={() => nav(user.role === "admin" ? "/admin" : user.role === "ambassador" ? "/ambassadeur/espace" : "/app")}
              className="group inline-flex items-center gap-1.5 border border-white/15 px-4 py-2 text-sm text-white transition-all hover:-translate-y-0.5 hover:border-cyan"
            >
              {t("nav_myspace")} <ArrowUpRight size={15} className="text-cyan transition-transform group-hover:translate-x-0.5" />
            </button>
          ) : (
            <button
              data-testid="nav-login-btn"
              onClick={() => nav("/connexion")}
              className="text-sm text-ink-secondary transition-colors hover:text-white"
            >
              {t("nav_login")}
            </button>
          )}
          <button
            data-testid="nav-cta-deposer"
            onClick={() => nav(user ? "/app/deposer" : "/inscription")}
            className="group inline-flex items-center gap-1.5 bg-cyan px-5 py-2.5 text-sm font-600 text-void transition-all hover:-translate-y-0.5 hover:glow-cyan"
          >
            {t("nav_deposit")}
          </button>
          <Link to="/" data-testid="nav-right-logo" className="ml-1 shrink-0">
            <img src="/aca-logo-white.png" alt="APP CHALLENGE ACADEMY" className="h-10 w-auto" />
          </Link>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <img src="/aca-logo-white.png" alt="ACA" className="h-8 w-auto" />
          <LanguageSwitcher compact />
          <button className="text-white" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="glass overflow-hidden border-t border-white/10 lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {LINKS.map((l) => (
                <Link key={l.to} to={l.to} className="py-2.5 text-ink-secondary hover:text-white" data-testid={`nav-mobile-${l.to === "/" ? "home" : l.to.slice(1)}`}>
                  {t(l.tk)}
                </Link>
              ))}
              <div className="mt-3 flex flex-col gap-2">
                {canInstall && (
                  <button onClick={openInstall} data-testid="nav-mobile-install-app" className="inline-flex items-center justify-center gap-1.5 border border-cyan/40 py-2.5 text-cyan">
                    <Download size={15} /> {t("pwa_menu")}
                  </button>
                )}
                <button onClick={() => nav(user ? (user.role === "admin" ? "/admin" : user.role === "ambassador" ? "/ambassadeur/espace" : "/app") : "/connexion")} className="border border-white/15 py-2.5 text-white">
                  {user ? t("nav_myspace") : t("nav_login")}
                </button>
                <button onClick={() => nav(user ? "/app/deposer" : "/inscription")} className="bg-cyan py-2.5 font-600 text-void">
                  {t("nav_deposit")}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
